"use client";

import { type ElementType } from "react";

import {
  List as ChakraList,
  type ListRootProps,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { Spacer, AspectRatio } from "@chakra-ui/react";

import { ReactNode } from "react";

import { Stack, HStack, Heading, Text, Box, Flex } from "../internal/exports";

import { Spinner } from "../spinner/spinner";

export interface ListProps extends ListRootProps {
  spacing?: number | string;
  styleType?: string;
  stylePosition?: string;
  loadMore?: ReactNode;
  loading?: boolean;
  title?: string;
  description?: string;
  isBordered?: boolean;
}

export interface ListItemProps extends HTMLChakraProps<"li"> {
  cover?: ReactNode;
  body?: ReactNode;
  leftActions?: Array<ReactNode>;
  rightActions?: Array<ReactNode>;
}

function ChakraUIList({
  spacing,
  gap,
  styleType = "none",
  stylePosition,
  css,
  ...props
}: ListProps & { children?: ReactNode; gap?: ListRootProps["gap"] }) {
  const suppressed = styleType === "none";
  const resolvedSpacing = gap ?? spacing;

  return (
    <ChakraList.Root
      listStyleType={styleType}
      listStylePosition={stylePosition}
      variant={suppressed ? "plain" : undefined}
      display="block"
      gap={0}
      css={{
        ...(resolvedSpacing != null
          ? {
              "& > *:not(style) ~ *:not(style)": {
                marginTop: resolvedSpacing
              }
            }
          : {}),
        ...(css as Record<string, unknown>)
      }}
      {...props}
    />
  );
}

function ChakraUIOrderedList({
  spacing,
  gap,
  as: _as,
  css,
  styleType = "decimal",
  stylePosition = "outside",
  ps = "1em",
  ...props
}: ListProps & {
  as?: ElementType;
  children?: ReactNode;
  gap?: ListRootProps["gap"];
}) {
  const resolvedSpacing = gap ?? spacing;

  return (
    <ChakraList.Root
      as="ol"
      display="block"
      gap={0}
      {...props}
      ps={ps}
      listStyleType={styleType}
      listStylePosition={stylePosition}
      variant={styleType === "none" ? "plain" : "marker"}
      css={{
        listStyleType: styleType,
        listStylePosition: stylePosition,
        "& > li": { display: "list-item" },
        "& > li::marker": { color: "inherit" },
        ...(resolvedSpacing != null
          ? {
              "& > *:not(style) ~ *:not(style)": {
                marginTop: resolvedSpacing
              }
            }
          : {}),
        ...(css as Record<string, unknown>)
      }}
    />
  );
}

function ChakraUIUnorderedList({
  spacing,
  gap,
  as: _as,
  css,
  styleType = "disc",
  stylePosition = "outside",
  ps = "1em",
  ...props
}: ListProps & {
  as?: ElementType;
  children?: ReactNode;
  gap?: ListRootProps["gap"];
}) {
  const resolvedSpacing = gap ?? spacing;

  return (
    <ChakraList.Root
      as="ul"
      display="block"
      gap={0}
      {...props}
      ps={ps}
      listStyleType={styleType}
      listStylePosition={stylePosition}
      variant={styleType === "none" ? "plain" : "marker"}
      css={{
        listStyleType: styleType,
        listStylePosition: stylePosition,
        "& > li": { display: "list-item" },
        "& > li::marker": { color: "inherit" },
        ...(resolvedSpacing != null
          ? {
              "& > *:not(style) ~ *:not(style)": {
                marginTop: resolvedSpacing
              }
            }
          : {}),
        ...(css as Record<string, unknown>)
      }}
    />
  );
}

function ChakraUIListItem(props: ListItemProps) {
  const { cover, body, leftActions, rightActions, ...rest } = props;
  void cover;
  void body;
  void leftActions;
  void rightActions;
  return <ChakraList.Item {...rest} />;
}

function ListIcon(props: HTMLChakraProps<"span">) {
  return <ChakraList.Indicator {...props} />;
}

const borderedStyles = {
  borderRadius: "0.5rem",
  border: "1px solid #E2E8F0",
  padding: { base: "1.5rem 1rem", lg: "2.5rem" },
  width: "100%"
};

const ListHead = ({
  title,
  description
}: Pick<ListProps, "title" | "description">) => {
  if (!title && !description) return null;

  return (
    <Box mb={10}>
      {title && <Heading size="md">{title}</Heading>}
      {description && <Text mt={2}>{description}</Text>}
    </Box>
  );
};

export function List(props: ListProps) {
  const {
    loadMore,
    loading,
    children,
    title,
    description,
    isBordered,
    styleType = "none",
    ...rest
  } = props;

  if (loading) {
    return (
      <Spinner>
        <ChakraUIList
          {...rest}
          {...(isBordered && borderedStyles)}
          styleType={styleType}
        >
          <ListHead title={title} description={description} />
          {children}
          {!!loadMore && <Box>{loadMore}</Box>}
        </ChakraUIList>
      </Spinner>
    );
  }

  return (
    <ChakraUIList
      {...rest}
      {...(isBordered && borderedStyles)}
      styleType={styleType}
    >
      <ListHead title={title} description={description} />
      {children}
      {!!loadMore && <Box>{loadMore}</Box>}
    </ChakraUIList>
  );
}

export function OrderedList(props: ListProps) {
  const {
    loadMore,
    loading,
    children,
    title,
    description,
    isBordered,
    ...rest
  } = props;

  if (loading) {
    return (
      <Spinner>
        <ChakraUIOrderedList {...rest} {...(isBordered && borderedStyles)}>
          <ListHead title={title} description={description} />
          {children}
          {!!loadMore && <Box>{loadMore}</Box>}
        </ChakraUIOrderedList>
      </Spinner>
    );
  }

  return (
    <ChakraUIOrderedList {...rest} {...(isBordered && borderedStyles)}>
      <ListHead title={title} description={description} />
      {children}
      {!!loadMore && <Box>{loadMore}</Box>}
    </ChakraUIOrderedList>
  );
}

export function UnorderedList(props: ListProps) {
  const {
    loadMore,
    loading,
    children,
    title,
    description,
    isBordered,
    ...rest
  } = props;

  if (loading) {
    return (
      <Spinner>
        <ChakraUIUnorderedList {...rest} {...(isBordered && borderedStyles)}>
          <ListHead title={title} description={description} />
          {children}
          {!!loadMore && <Box>{loadMore}</Box>}
        </ChakraUIUnorderedList>
      </Spinner>
    );
  }

  return (
    <ChakraUIUnorderedList {...rest} {...(isBordered && borderedStyles)}>
      <ListHead title={title} description={description} />
      {children}
      {!!loadMore && <Box>{loadMore}</Box>}
    </ChakraUIUnorderedList>
  );
}

export function ListItem(props: ListItemProps) {
  const { cover, body, leftActions, rightActions, ...rest } = props;

  if (cover || body || leftActions || rightActions) {
    return (
      <ChakraUIListItem {...rest}>
        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          {cover && (
            <AspectRatio
              minWidth={{ md: 36 }}
              ratio={{ base: 16 / 9, lg: 4 / 3 }}
              css={{ "& > img": { borderRadius: "lg" } }}
            >
              {cover}
            </AspectRatio>
          )}
          {(body || leftActions || rightActions) && (
            <Stack width="100%" gap={4}>
              <Box css={{ "& > p": { fontSize: "md" } }}>{body}</Box>
              <Flex
                gap={2}
                align="flex-start"
                css={{ "& > *": { fontSize: "xs", fontWeight: "medium" } }}
              >
                {leftActions && (
                  <>
                    <Flex rowGap={2} columnGap={4}>
                      {leftActions.map((action, index) => (
                        <HStack
                          gap={0}
                          key={`leftAction-${index}`}
                          css={{
                            "*": { maxHeight: 4 },
                            svg: { width: 4, height: 4 }
                          }}
                        >
                          {action}
                        </HStack>
                      ))}
                    </Flex>
                    <Spacer />
                  </>
                )}
                {rightActions && (
                  <Flex
                    wrap="wrap"
                    rowGap={2}
                    columnGap={4}
                    justify="flex-end"
                    css={{ "&>[role='button']": { cursor: "pointer" } }}
                  >
                    {rightActions.map((action, index) => (
                      <HStack
                        gap={0}
                        key={`rightAction-${index}`}
                        css={{
                          "*": { maxHeight: 4 },
                          svg: { width: 4, height: 4 }
                        }}
                      >
                        {action}
                      </HStack>
                    ))}
                  </Flex>
                )}
              </Flex>
            </Stack>
          )}
        </Stack>
      </ChakraUIListItem>
    );
  }

  return <ChakraUIListItem {...rest} />;
}

export { ListIcon };
